import { createApi } from '@reduxjs/toolkit/query/react';

import { UsersApi } from '.';

import { RootState } from '../store';

import { getData, postData } from '../../services/API';
import {
  encountersUrl,
  getApiVersionUrl,
  dischargeUrl,
  getUserRolesUrl,
  revokeUserAccessUrl,
  usersUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';

import {
  formatRoundingType,
  toTitleCase,
  unCamelCase
} from '../../utils/helpers';

import { IFacility, UserList } from '../../config/interfaces';

interface IGetUsersPayload {
  accessLevel: string;
  accessLevelId: string;
  hcaId: string;
  name: string;
  page: string;
  role: string;
}

/**
 * RTK Query API slice for admin features.
 *
 * @remarks
 * Provides endpoints for:
 * - Version info (`getVersion`)
 * - Paginated users with full name (`getUsers`)
 * - Revoke user access (`postRevokeUserAccess`)
 * - Discharge patients (`postDischargePatients`)
 * - User roles, formatted (`getUserRoles`)
 * - Encounters (`getEncounters`)
 *
 * @example
 * import { useGetUsersQuery } from 'src/redux/apis/Admin.api';
 * const { data } = useGetUsersQuery({ page: '1' });
 */
export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: (operation: any) => operation,
  tagTypes: ['encounters', 'roles', 'users', 'users-download', 'version'],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    getVersion: builder.query({
      query: () => getData(getApiVersionUrl, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: () => [{ type: 'version' }]
    }),
    getUsers: builder.query({
      query: (payload: IGetUsersPayload & Record<string, string>) => {
        const params = new URLSearchParams(payload);
        return getData(`${usersUrl}?${params.toString()}`, {});
      },
      transformResponse: response => ({
        ...response,
        paginatedUserList:
          response.paginatedUserList?.map((user: any) => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
          })) || []
      }),
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'users', id: 'LIST' },
        { type: 'users', id: JSON.stringify(payload) }
      ]
    }),
    getUsersForDownload: builder.query({
      // TODO: Possibly move this to the users list component
      queryFn: async (
        payload: IGetUsersPayload & Record<string, string>,
        store
      ) => {
        const params = new URLSearchParams(payload);
        try {
          const response: any = await getData(
            `${usersUrl}?${params.toString()}`,
            {}
          );
          if (response.error) {
            return { error: response.error };
          }

          // Retrieve facilities from the store for now
          const { facilities }: { facilities: any[] } = (
            store.getState() as RootState
          ).ConfigReducer;

          return {
            data: response.data.paginatedUserList.map((user: UserList) => {
              return {
                employee:
                  toTitleCase(`${user.firstName} ${user.lastName}`) ?? '',
                '3/4 ID': user.hcaid,
                userRole: user.roles.map(role => unCamelCase(role)),
                accessLevel:
                  user.access.accessLevel === 'master'
                    ? 'Corporate'
                    : toTitleCase(user.access.accessLevel) ?? '',
                accessPlaces:
                  user.access.accessLevel === 'facility'
                    ? user.access.accessLevelIds
                        .reduce((acc: string[], id: string) => {
                          const facilityObj = facilities.find(
                            (o: IFacility) => o.facilityId === id
                          );
                          return facilityObj
                            ? [...acc, facilityObj?.facility]
                            : acc;
                        }, [])
                        .toString()
                    : user.access.accessLevelIds.toString(),
                position: user.title
                  ? `${user.title}${user.dept ? `, ${user.dept}` : ''}`
                  : 'Unknown'
              };
            })
          };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, payload) => [
        { type: 'users-download', id: JSON.stringify(payload) }
      ]
    }),
    postRevokeUserAccess: builder.mutation({
      query: (userId: string) => postData(revokeUserAccessUrl, { userId }),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: [{ type: 'users', id: 'LIST' }]
    }),
    postDischargePatients: builder.mutation({
      query: payload =>
        postData(dischargeUrl, {
          ...payload,
          accountNumbers: payload.accountNumbers.join(',')
        }),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          UsersApi.util.invalidateTags([
            {
              type: 'patients-to-round-facility',
              id: payload.facilityId
            }
          ])
        );
      }
    }),
    getUserRoles: builder.query({
      query: () => getData(getUserRolesUrl, {}),
      transformResponse: ({ data }) =>
        data.map((item: string) => {
          return {
            id: item,
            value: formatRoundingType(item)
          };
        }),
      transformErrorResponse: processError,
      providesTags: () => [{ type: 'roles' }]
    }),
    getEncounters: builder.query({
      query: (payload: any) => {
        const params = new URLSearchParams(payload);
        return getData(`${encountersUrl}?${params.toString()}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'encounters', id: JSON.stringify(payload) }
      ]
    })
  })
});

export const {
  useGetVersionQuery,
  useLazyGetVersionQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUsersForDownloadQuery,
  useLazyGetUsersForDownloadQuery,
  usePostRevokeUserAccessMutation,
  usePostDischargePatientsMutation,
  useGetUserRolesQuery,
  useLazyGetUserRolesQuery,
  useGetEncountersQuery,
  useLazyGetEncountersQuery
} = adminApi;
